import { TowerSpec, ProjectileSpawn, ShotKind } from './tower';
import { TowerType } from './types';
import { Vec2, dist } from './vec';
import type { Path } from './path';
import { EnemyState, damageEnemy, rollDodge } from './enemy';
import { enemiesInRadius } from './targeting';
import { applyStatus } from './status';

/**
 * Impact forgiveness radii (px). Widened so the (deliberately slow, easy-to-see)
 * projectiles still reliably connect with fast enemies despite travel time —
 * keeps visual speed low without turning every shot at a dino into a whiff.
 */
export const SPLASH_SINGLE = 30;
export const SPLASH_DEBUFF = 34;
export const PIERCE_HIT_R = 20;
/** The lightning cloud is fat (56 px on screen), so it connects generously. */
export const PATH_SHOT_HIT_R = 28;

export interface ProjectileState {
  id: number;
  pos: Vec2;
  from: Vec2;
  to: Vec2;
  speed: number;
  behavior: TowerSpec['behavior'];
  spec: TowerSpec;
  towerType: TowerType;
  tier: 1 | 2 | 3 | 4;
  alive: boolean;
  hitIds: number[]; // pierce: enemy ids already hit (dedup + count)
  /** True for a timed special shot — impacts use spec.special.damage. */
  special?: boolean;
  /** `shotRoll` towers only: the band rolled when this bullet was fired. */
  shotKind?: ShotKind;
  /** `shotRoll` towers only: damage spent on impact instead of `spec.damage`. */
  damageOverride?: number;
  /**
   * Set on impact when the bullet actually found an enemy. A shot that whiffs
   * must not announce a headshot, and the render layer has no other way to
   * tell: by the time a projectile leaves the sim's list, its view has already
   * been updated past the moment of contact.
   */
  didHit?: boolean;
  /**
   * Path-shot (Lightning Hambi's cloud) only: how far along the track the
   * cloud sits. It starts at the path's LENGTH (the player's base) and counts
   * DOWN to 0 (the spawn end) — against the traffic. The sim moves it with
   * `updatePathShot`; `pos` follows. Undefined for every other projectile.
   */
  pathDist?: number;
  /** Path-shot only: which route the cloud is rolling up (see ProjectileSpawn). */
  route?: number;
  /**
   * Path-shot only: General Capy's RED imposter, rolled at fire time. Carries
   * `damageOverride` with it; the render layer reads this to pick the red
   * running frames.
   */
  redSpawn?: boolean;
  /**
   * Bow Hambi's poison arrow: on contact it deals NO impact damage and
   * poisons the enemy (`spec.poisonArrow`) instead. Rolled at fire time.
   */
  poison?: boolean;
}

export function applyEffects(e: EnemyState, spec: TowerSpec, damage = spec.damage): void {
  // A dodged hit misses outright: no damage and no status (The Mushy).
  if (rollDodge(e)) return;
  if (damage > 0) damageEnemy(e, damage);
  if (spec.burn)
    applyStatus(e.statuses, {
      kind: 'burn',
      magnitude: spec.burn.magnitude,
      remaining: spec.burn.duration,
    });
  if (spec.vulnerable)
    applyStatus(e.statuses, {
      kind: 'vulnerable',
      magnitude: spec.vulnerable.magnitude,
      remaining: spec.vulnerable.duration,
    });
}

export function createProjectile(id: number, spawn: ProjectileSpawn): ProjectileState {
  return {
    id,
    pos: { x: spawn.from.x, y: spawn.from.y },
    from: { x: spawn.from.x, y: spawn.from.y },
    to: { x: spawn.to.x, y: spawn.to.y },
    speed: spawn.speed,
    behavior: spawn.spec.behavior,
    spec: spawn.spec,
    towerType: spawn.towerType,
    tier: spawn.tier,
    alive: true,
    hitIds: [],
    special: spawn.special,
    shotKind: spawn.shotKind,
    damageOverride: spawn.damageOverride,
    pathDist: spawn.pathDist,
    route: spawn.route,
    redSpawn: spawn.redSpawn,
    poison: spawn.poison,
  };
}

/**
 * Advance a path-shot (Lightning Hambi's cloud) one tick: strike every enemy
 * within PATH_SHOT_HIT_R that it has not struck yet, then roll `speed * dt`
 * further towards the spawn end. Returns true while still rolling; false once
 * it has struck `spec.pierce` enemies or reached the spawn end (it despawns
 * either way). Never call `updateProjectile` on one of these — that would fly
 * it in a straight line from the base to the spawn.
 */
export function updatePathShot(
  p: ProjectileState,
  dt: number,
  path: Path,
  enemies: EnemyState[],
): boolean {
  if (!p.alive || p.pathDist === undefined) return false;
  const max = p.spec.pierce ?? 1;
  for (const e of enemies) {
    if (!e.alive || p.hitIds.includes(e.id)) continue;
    if (dist(e.pos, p.pos) <= PATH_SHOT_HIT_R) {
      // `damageOverride` is General Capy's red imposter (750 whatever the
      // tier). Lightning Hambi never sets one, so he is unaffected.
      applyEffects(e, p.spec, p.damageOverride ?? p.spec.damage);
      p.hitIds.push(e.id);
      p.didHit = true;
      if (p.hitIds.length >= max) {
        p.alive = false;
        return false;
      }
    }
  }
  p.pathDist -= p.speed * dt;
  if (p.pathDist <= 0) {
    p.pathDist = 0;
    p.pos = path.pointAt(0);
    p.alive = false;
    return false;
  }
  p.pos = path.pointAt(p.pathDist);
  return true;
}

function closestWithin(point: Vec2, radius: number, enemies: EnemyState[]): EnemyState | null {
  let best: EnemyState | null = null;
  let bestD = radius;
  for (const e of enemies) {
    if (!e.alive) continue;
    const d = dist(point, e.pos);
    if (d <= bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

/**
 * The two enemies flanking `hit` on the path: the nearest living one AHEAD of it
 * (further along the route, i.e. closer to the exit) and the nearest one BEHIND.
 * Either may be absent — a lone leader has nothing in front of it.
 *
 * Ordering is by `distance` travelled, NOT by proximity to the impact point, so
 * "in front" always means what the player sees it mean: the next enemy down the
 * track. Ties in distance are broken by id only through `<` / `>`, which simply
 * means one of two perfectly overlapping enemies is chosen deterministically.
 */
export function pathNeighbors(hit: EnemyState, enemies: EnemyState[]): EnemyState[] {
  let ahead: EnemyState | null = null;
  let behind: EnemyState | null = null;
  for (const e of enemies) {
    if (!e.alive || e.id === hit.id) continue;
    if (e.distance > hit.distance) {
      if (!ahead || e.distance < ahead.distance) ahead = e;
    } else if (e.distance < hit.distance) {
      if (!behind || e.distance > behind.distance) behind = e;
    }
  }
  return [ahead, behind].filter((e): e is EnemyState => e !== null);
}

/**
 * Leafstorm Hambi's tornado lands: the `tornado.targets` living enemies
 * nearest the impact point, among those inside the tower's `range` of its
 * plot (`p.from`), each take the full shot. His target is normally the nearest
 * of all, since the shot was led onto it. With nobody in range it whiffs.
 */
export function tornadoVictims(p: ProjectileState, enemies: EnemyState[]): EnemyState[] {
  const n = p.spec.tornado?.targets ?? 1;
  return enemies
    .filter(
      (e) => e.alive && (e.ignoreRange || dist(p.from, e.pos) <= p.spec.range + SPLASH_SINGLE),
    )
    .sort((a, b) => dist(a.pos, p.to) - dist(b.pos, p.to) || a.id - b.id)
    .slice(0, n);
}

function resolveTornado(p: ProjectileState, enemies: EnemyState[]): void {
  const damage = p.damageOverride ?? p.spec.damage;
  for (const e of tornadoVictims(p, enemies)) {
    applyEffects(e, p.spec, damage);
    p.hitIds.push(e.id);
    p.didHit = true;
  }
}

function resolveImpact(p: ProjectileState, enemies: EnemyState[]): void {
  switch (p.behavior) {
    // A path-shot USUALLY resolves in flight, in `updatePathShot`, and never
    // reaches here. The exception is Lightning Hambi's bolt at an anchored boss
    // (`updateBoltShotTower`): that one flies plot-to-target like any other
    // shot, so it lands like one.
    case 'path-shot':
    // Samurai Slicer is melee, but his thrown daggers are ordinary flying
    // shots and land like one (`TowerSpec.midStrike`). No other melee tower
    // ever spawns a projectile.
    case 'melee':
    case 'single': {
      // Leafstorm Hambi's tornado spins out into the nearest enemies instead.
      if (p.spec.tornado) {
        resolveTornado(p, enemies);
        break;
      }
      const e = closestWithin(p.to, SPLASH_SINGLE, enemies);
      if (e) {
        if (p.poison && p.spec.poisonArrow) {
          // A poison arrow lands for nothing and poisons instead — the whole
          // hit is the damage-over-time (spec Revision 7: 400/s for 3 s).
          applyStatus(e.statuses, {
            kind: 'poison',
            magnitude: p.spec.poisonArrow.magnitude,
            remaining: p.spec.poisonArrow.duration,
          });
          p.didHit = true;
          break;
        }
        const damage =
          p.special && p.spec.special ? p.spec.special.damage : (p.damageOverride ?? p.spec.damage);
        applyEffects(e, p.spec, damage);
        // The Aqua Sorcerer's droplet also soaks the enemy in front and the one
        // behind for the same damage. Damage only — the statuses in
        // `applyEffects` belong to the enemy the droplet actually struck.
        //
        // Galactic Capy splashes on his BLACK fireball alone, so a special
        // shot asks its own flag: the Aqua Sorcerer has no `special` at all
        // and keeps the spec-wide one.
        const splash = p.special ? p.spec.special?.neighborSplash : p.spec.neighborSplash;
        if (splash)
          for (const n of pathNeighbors(e, enemies)) if (!rollDodge(n)) damageEnemy(n, damage);
        p.didHit = true;
      }
      break;
    }
    case 'debuff': {
      const e = closestWithin(p.to, SPLASH_DEBUFF, enemies);
      if (e) {
        applyEffects(e, p.spec);
        p.didHit = true;
      }
      break;
    }
    case 'aoe': {
      for (const e of enemiesInRadius(p.to, p.spec.aoeRadius ?? 0, enemies)) {
        applyEffects(e, p.spec);
        p.didHit = true;
      }
      break;
    }
    case 'pierce':
    case 'aura-slow':
      break; // pierce resolves in-flight; aura-slow never becomes a projectile
  }
}

/** Advance one projectile. Returns true while still in flight, false once it impacts/fizzles. */
export function updateProjectile(p: ProjectileState, dt: number, enemies: EnemyState[]): boolean {
  if (!p.alive) return false;

  // Pierce resolves continuously along the flight path.
  if (p.behavior === 'pierce') {
    const max = p.spec.pierce ?? 1;
    for (const e of enemies) {
      if (!e.alive || p.hitIds.includes(e.id)) continue;
      if (dist(e.pos, p.pos) <= PIERCE_HIT_R) {
        applyEffects(e, p.spec);
        p.hitIds.push(e.id);
        if (p.hitIds.length >= max) {
          p.alive = false;
          return false;
        }
      }
    }
  }

  const dx = p.to.x - p.pos.x;
  const dy = p.to.y - p.pos.y;
  const remaining = Math.hypot(dx, dy);
  const travel = p.speed * dt;

  if (remaining > 0 && travel < remaining) {
    p.pos = { x: p.pos.x + (dx / remaining) * travel, y: p.pos.y + (dy / remaining) * travel };
    return true;
  }

  // Reached the impact point this tick.
  p.pos = { x: p.to.x, y: p.to.y };
  resolveImpact(p, enemies);
  p.alive = false;
  return false;
}
